import { useAuthRestApi } from '../common';
import { Observable } from 'rxjs';
import { IDisclosure, IDisclosuresApi, IParticipatingOrganizationDto } from '@vivli/features/disclosures/infrastructure/interface';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { ICitation, ICitationDoiDto } from '@vivli/shared/features/citations/infrastructure/interface';

export const useDisclosuresApi = (): IDisclosuresApi => {
    const { handleGet, handlePost, handlePut, handleDelete } = useAuthRestApi();
    const getDisclosures = (dataRequestId: string): Observable<IDisclosure[]> => {
        return handleGet<IDisclosure[]>(`/disclosures/list?dataRequestId=${dataRequestId}`);
    };

    const getDisclosure = (disclosureId: string): Observable<IDisclosure> => {
        return handleGet<IDisclosure>(`/disclosures/${disclosureId}`);
    };

    const getDisclosureByVivliId = (vivliId: string): Observable<IDisclosure> => {
        return handleGet<IDisclosure>(`/disclosures/vivliId/${vivliId}`);
    };

    const createDisclosure = (disclosure: IDisclosure): Observable<IDisclosure> => {
        return handlePost<IDisclosure>(`/disclosures`, disclosure);
    };

    const saveDisclosure = (disclosure: IDisclosure): Observable<IDisclosure> => {
        return handlePut<IDisclosure>(`/disclosures`, disclosure);
    };

    const changeDisclosureStatus = (disclosure: IDisclosure, status: DisclosureStatusEnum): Observable<IDisclosure> => {
        return handlePut<IDisclosure>(`/disclosures/changestatus/${disclosure.id}/${status}`, disclosure);
    };

    const finalizeDisclosure = (
        disclosureId: string,
        participatingOrganizationDto: IParticipatingOrganizationDto
    ): Observable<IDisclosure> => {
        return handlePut<IDisclosure>(`/disclosures/${disclosureId}/finalized`, participatingOrganizationDto);
    };

    const createCitationFromDoi = (
        workDoi: string,
        dataRequestId: string,
        dataRequestDoiStem: string,
        disclosureId: string
    ): Observable<ICitation> => {
        const workDoiDto: ICitationDoiDto = { citationDoi: workDoi };
        return handlePost<ICitation>(`/disclosures/providedDoi/${dataRequestId}/${dataRequestDoiStem}/${disclosureId}`, workDoiDto);
    };

    const createCitationFromObject = (
        citation: ICitation,
        dataRequestId: string,
        dataRequestDoiStem: string,
        disclosureId: string
    ): Observable<ICitation> => {
        return handlePost<ICitation>(`/disclosures/providedCitation/${dataRequestId}/${dataRequestDoiStem}/${disclosureId}`, citation);
    };

    const updateCitation = (citation: ICitation, citationId: string, dataRequestDoiStem: string): Observable<ICitation> => {
        return handlePut<ICitation>(`disclosures/${citationId}/citation/${dataRequestDoiStem}`, citation);
    };

    const deleteCitation = (citationId: string, dataRequestId: string, dataRequestDoiStem: string): Observable<ICitation> => {
        return handleDelete<ICitation>(`disclosures/${citationId}/citation/${dataRequestId}/${dataRequestDoiStem}`);
    };

    const deleteDisclosure = (disclosureId: number) => {
        return handleDelete<void>(`/disclosures/${disclosureId}/disclosure`);
    };

    return {
        getDisclosures,
        getDisclosure,
        getDisclosureByVivliId,
        createDisclosure,
        saveDisclosure,
        changeDisclosureStatus,
        finalizeDisclosure,
        createCitationFromDoi,
        createCitationFromObject,
        updateCitation,
        deleteCitation,
        deleteDisclosure
    };
};
